import { flow, types } from "mobx-state-tree";

import Utils from "pages/common/lib/grabyo-util";
import Constants from "const/constants";
import { setPublicRuntimeConfigToSecuredConfig } from "config/config";
import UploadService from "api/upload";

import { RemoteFileStore, FileStore } from "./upload";

export const UPLOAD_ID_LENGTH = 5;

const { uploadStatusTypes } = Constants;

/**
 * Uploader store, adds/removes uploads and contains information about their state
 * @typedef {Object} UploaderStore
 */
const UploaderStore = types
    .model({
        context: ``,
        uploads: types.map(types.union(FileStore, RemoteFileStore)),
        organizationId: types.string
    })
    .volatile(() => ({
        uploadService: null
    }))
    .views((self) => ({
        /**
         * @property
         * @summary - Returns an array of all uploads
         * @returns {Array<RemoteFileStore|FileStore>}
         */
        get uploadsArray() {
            return Utils.mapToArray(self.uploads).reverse();
        }
    }))
    .views((self) => ({
        get selectableFileSources() {
            return [`local_file_system`, `url`];
        },

        /**
         * @property
         * @summary - Checks whether all uploads are valid
         * @returns {Boolean}
         */
        get allUploadsValid(): boolean {
            if (self.uploadsArray.length === 0) return false;
            return self.uploadsArray.every(
                ({ status }) =>
                    status === uploadStatusTypes.VALID ||
                    status === uploadStatusTypes.UPLOADED
            );
        },

        /**
         * @property
         * @summary - Returns an array of valid uploads
         * @returns {Array<RemoteFileStore|FileStore>}
         */
        get validUploadsArray() {
            return (
                self.uploadsArray.filter(
                    ({ status, progress }) =>
                        status === `valid` && progress === 1
                ) || []
            );
        },

        /**
         * @property
         * @summary - Returns an array of failed uploads
         * @returns {Array<RemoteFileStore|FileStore>}
         */
        get failedUploadsArray() {
            return (
                self.uploadsArray.filter(({ status }) => status === `failed`) ||
                []
            );
        },

        /**
         * @property
         * @summary - Returns an array of cancelled uploads
         * @returns {Array<RemoteFileStore|FileStore>}
         */
        get cancelledUploadsArray() {
            return (
                self.uploadsArray.filter(
                    ({ status }) => status === `cancelled`
                ) || []
            );
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Adds an upload
         * @param {Blob|Object} data The data to be assigned to an upload
         * @returns {FileStore|RemoteFileStore}
         */
        addUpload(data, maxUploadSizeAllowed = undefined) {
            const id = Utils.generateGrabyoId(UPLOAD_ID_LENGTH);

            const hasValidSize = maxUploadSizeAllowed
                ? data.size < maxUploadSizeAllowed
                : true;

            const upload = {
                id,
                fileName: data.name,
                description: data.description,
                fileSize: data.size,
                mimetype: data.mimetype,
                status: hasValidSize ? data.status : `invalid`,
                errorMessages: data.errorMessages,
                storedURL: data.storedURL,
                aspectRatio: data.aspectRatio,

                // This should only be set in the case of URL upload
                url: data.url,
                thumbnailUrl: data.thumbnailUrl
            };
            self.uploads.set(upload.id, upload);

            // If uploading local file
            if (data instanceof Blob) {
                //@ts-ignore: `setFile` only exists on `FileStore`
                self.uploads.get(upload.id).setFile(data);
            }

            // only return instance if size is Valid
            if (hasValidSize) {
                return self.uploads.get(upload.id);
            }
            return undefined;
        },
        getUploadService: flow(function* getUploadService() {
            if (self.uploadService) {
                return self.uploadService;
            } else {
                yield setPublicRuntimeConfigToSecuredConfig();
                self.uploadService = new UploadService(self.organizationId);
                return self.uploadService;
            }
        }),
        /**
         * @method
         * @summary - Stops and removes an upload
         * @param {String} uploadId
         */
        removeUpload(uploadId: string) {
            const upload = self.uploads.get(uploadId);
            if (upload.status !== uploadStatusTypes.CANCELLED) {
                upload.stop();
            }
            self.uploads.delete(uploadId);
        },
        /**
         * @method
         * @summary - Removes all uploads from the store
         */
        removeAllUploads() {
            self.uploadsArray.forEach((upload) => {
                if (upload.status !== uploadStatusTypes.CANCELLED) {
                    upload.stop();
                }
            });

            self.uploads.clear();
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Adds and starts an upload
         * @param {File|Object} data File data
         */
        uploadFile(data, maxUploadSizeAllowed = undefined) {
            const upload = self.addUpload(data, maxUploadSizeAllowed);

            // If upload instance is returned then call start
            if (upload) {
                upload.start(self.context);
            }
        }
    }))
    .actions((self) => ({
        /**
         * @method
         * @summary - Initiates a file upload from Filestack
         * @param {Boolean} singular Whether only one upload should be present at all times
         */
        uploadFromFilestack: flow(function* uploadFromFilestack(
            singular = true
        ) {
            const uploadService = yield self.getUploadService();
            const instance = uploadService.openFilestack({
                exposeOriginalFile: true,

                // On file selected, for imports from URL
                onFileSelected: (file) => {
                    if (self.selectableFileSources.includes(file.source)) {
                        if (self.uploadsArray[0] && singular) {
                            self.removeUpload(self.uploadsArray[0].id);
                        }
                    }

                    if (file.source === `local_file_system`) {
                        file.originalFile.name =
                            file.originalFile.name.replaceAll(`/`, `-`);
                        self.uploadFile(file.originalFile);
                        instance.close();
                    }

                    if (file.source === `url`) {
                        self.uploadFile({
                            name: file.originalPath.split(`/`).pop(),
                            url: file.originalPath,
                            mimetype: file.mimetype
                        });
                        instance.close();
                    }
                },

                // On file upload, when selecting files from external providers e.g. Google drive
                // These are treated like URL imports by our stores
                onUploadDone: ({ filesUploaded }) => {
                    if (self.uploadsArray.length > 0 && singular) {
                        self.removeUpload(self.uploadsArray[0].id);
                    }
                    filesUploaded.forEach((file) => {
                        self.uploadFile({
                            name: file.filename,
                            url: `${Constants.CDN_URL}/${file.key}`,
                            storedURL: `${Constants.CDN_URL}/${file.key}`,
                            mimetype: file.mimetype
                        });
                    });
                    instance.close();
                }
            });
        }),
        setUploadService(service) {
            self.uploadService = service;
        }
    }));

export default UploaderStore;
